import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { notifyGlobal } from '@/context/notificationContext';
import { productService } from '@/services/productService';
import type {
  CreateProductRequest,
  Product,
  UpdateProductRequest,
} from '@/types/product';

const productKeys = {
  all: ['products'] as const,
  byId: (id: string) => ['products', id] as const,
};

export function useProducts() {
  return useQuery({
    queryKey: productKeys.all,
    queryFn: productService.getProducts,
  });
}

export function useProduct(productId?: string) {
  return useQuery({
    queryKey: productKeys.byId(productId ?? ''),
    queryFn: () => productService.getProduct(productId ?? ''),
    enabled: Boolean(productId),
  });
}

export function useCreateProduct() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('entity-portal');

  return useMutation({
    mutationFn: (data: CreateProductRequest) =>
      productService.createProduct(data),
    onMutate: async (newProduct) => {
      await queryClient.cancelQueries({ queryKey: productKeys.all });
      const previousProducts = queryClient.getQueryData<Product[]>(
        productKeys.all,
      );

      const optimisticProduct: Product = {
        ...newProduct,
        id: `temp-${Date.now()}`,
        isActive: newProduct.isActive ?? true,
        sortOrder: newProduct.sortOrder ?? 0,
      };

      queryClient.setQueryData<Product[]>(productKeys.all, (current = []) => [
        ...current,
        optimisticProduct,
      ]);

      return { previousProducts };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousProducts) {
        queryClient.setQueryData(productKeys.all, context.previousProducts);
      }
    },
    onSuccess: (createdProduct) => {
      queryClient.setQueryData<Product[]>(productKeys.all, (current = []) =>
        current.map((item) =>
          item.id.startsWith('temp-') && item.name === createdProduct.name
            ? createdProduct
            : item,
        ),
      );
      notifyGlobal(t('product.productCreatedSuccess'), 'success');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });
}

export function useUpdateProduct() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('entity-portal');

  return useMutation({
    mutationFn: ({
      productId,
      data,
    }: {
      productId: string;
      data: UpdateProductRequest;
    }) => productService.updateProduct(productId, data),
    onMutate: async ({ productId, data }) => {
      await queryClient.cancelQueries({ queryKey: productKeys.all });
      await queryClient.cancelQueries({
        queryKey: productKeys.byId(productId),
      });

      const previousProducts = queryClient.getQueryData<Product[]>(
        productKeys.all,
      );
      const previousProduct = queryClient.getQueryData<Product>(
        productKeys.byId(productId),
      );

      queryClient.setQueryData<Product[]>(productKeys.all, (current = []) =>
        current.map((item) =>
          item.id === productId
            ? {
                ...item,
                ...data,
              }
            : item,
        ),
      );

      if (previousProduct) {
        queryClient.setQueryData<Product>(productKeys.byId(productId), {
          ...previousProduct,
          ...data,
        });
      }

      return { previousProducts, previousProduct };
    },
    onError: (_error, variables, context) => {
      if (context?.previousProducts) {
        queryClient.setQueryData(productKeys.all, context.previousProducts);
      }

      if (context?.previousProduct) {
        queryClient.setQueryData(
          productKeys.byId(variables.productId),
          context.previousProduct,
        );
      }
    },
    onSuccess: (updatedProduct) => {
      queryClient.setQueryData<Product[]>(productKeys.all, (current = []) =>
        current.map((item) =>
          item.id === updatedProduct.id ? updatedProduct : item,
        ),
      );
      queryClient.setQueryData(
        productKeys.byId(updatedProduct.id),
        updatedProduct,
      );
      notifyGlobal(t('product.productUpdatedSuccess'), 'success');
    },
    onSettled: (_result, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: productKeys.all });
      queryClient.invalidateQueries({
        queryKey: productKeys.byId(variables.productId),
      });
    },
  });
}

export function useDeleteProduct() {
  const queryClient = useQueryClient();
  const { t } = useTranslation('entity-portal');

  return useMutation({
    mutationFn: (productId: string) => productService.deleteProduct(productId),
    onMutate: async (productId) => {
      await queryClient.cancelQueries({ queryKey: productKeys.all });
      const previousProducts = queryClient.getQueryData<Product[]>(
        productKeys.all,
      );

      queryClient.setQueryData<Product[]>(productKeys.all, (current = []) =>
        current.filter((item) => item.id !== productId),
      );

      return { previousProducts };
    },
    onError: (_error, _productId, context) => {
      if (context?.previousProducts) {
        queryClient.setQueryData(productKeys.all, context.previousProducts);
      }
    },
    onSuccess: (_result, productId) => {
      queryClient.removeQueries({ queryKey: productKeys.byId(productId) });
      notifyGlobal(t('product.productDeletedSuccess'), 'success');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });
}

